import {createAction} from '@reduxjs/toolkit'
import {NavigationType, getHeadlessUrl} from '@wix/wix-to-headless-redirect-client'
import {IWixAPI} from '@wix/yoshi-flow-editor'
import {navigateToDetails, navigateToForm} from '../../components/details-page/Widget/actions/navigation'
import {inDetails} from '../../components/details-page/Widget/selectors/navigation'
import {createAsyncAction} from '../../components/details-page/Widget/services/redux-toolkit'
import {getEventListPageUrl} from '../services/navigation'
import {EnvironmentConfig} from '../types/state'
import {
  getLanguage,
  getLocale,
  getTimezone,
  isEditor,
  isEditor3,
  isMobile,
  isPreview,
  isSite,
} from '../utils/wix-code-api'

const HEADLESS_QUERY_PARAM = 'headlessExternalUrls'

export const setBaseEnvironment = createAsyncAction<Omit<EnvironmentConfig, 'formViewState'>, void>(
  'SET_BASE_ENVIRONMENT',
  async (_, {getState, extra: {wixCodeApi}}) => {
    const state = getState()

    const headless = Boolean(wixCodeApi.location.query?.[HEADLESS_QUERY_PARAM])
    const headlessOrigin = getHeadlessUrlByName(wixCodeApi, NavigationType.HOME)
    const hasHeadlessTYP = headless && Boolean(getHeadlessUrlByName(wixCodeApi, NavigationType.EVENTS_THANK_YOU_PAGE))
    const homePageUrl = headless ? headlessOrigin : wixCodeApi.location.baseUrl
    const eventListPageUrl = await getEventListPageUrl(wixCodeApi)

    return {
      seo: wixCodeApi.seo.isInSEO(),
      locale: getLocale(wixCodeApi) || state.siteSettings.locale,
      language: getLanguage(wixCodeApi),
      timezone: getTimezone(wixCodeApi),
      mobile: isMobile(wixCodeApi),
      editor: isEditor(wixCodeApi),
      preview: isPreview(wixCodeApi),
      site: isSite(wixCodeApi),
      eventListPageUrl: eventListPageUrl?.url ?? homePageUrl,
      eventListPageRelativeUrl: eventListPageUrl?.relativeUrl,
      homePageUrl,
      headless,
      hasHeadlessTYP,
    }
  },
)

export const setFormFactor = createAction('SET_FORM_FACTOR', (formFactor: string) => ({
  payload: formFactor === 'Mobile',
}))

export const handleFormViewState = createAsyncAction<boolean, boolean>(
  'HANDLE_FORM_VIEW_STATE',
  async (formViewState, {getState, dispatch}) => {
    const state = getState()
    const detailsPage = inDetails(state)

    if (formViewState) {
      dispatch(navigateToForm())
    } else if (!detailsPage) {
      dispatch(navigateToDetails())
    }

    return formViewState
  },
  {
    condition: (_, {extra: {wixCodeApi}}) => isEditor(wixCodeApi) && isEditor3(wixCodeApi),
  },
)

const getHeadlessUrlByName = (wixCodeApi: IWixAPI, logicalName: NavigationType) =>
  getHeadlessUrl({
    query: wixCodeApi.location.query,
    baseUrl: wixCodeApi.location.baseUrl,
    navParams: {
      logicalName,
    },
  })
